import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useGetDriverPaymentsQuery } from "../../../Api/attoDeskApi";
import { IDriverPayment } from "../../../Api/Interface/api.interface";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PrintIcon from "@mui/icons-material/Print";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";

const BRAND = "#2f3891";

const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b",
  textTransform: "uppercase" as const, letterSpacing: "0.06em",
  py: 1.25, px: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.5, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "top" as const,
};

const DriverReviewsList: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchText.trim()) params.search = searchText.trim();
      const response = await axios.get(`${API_URL}/pdf/driver-payment-reviews`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `driver-reviews-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  const { data, isLoading } = useGetDriverPaymentsQuery({ searchText: searchText || undefined });

  const allPayments: IDriverPayment[] = useMemo(() => {
    if (!data || !Array.isArray(data.data)) return [];
    return data.data as IDriverPayment[];
  }, [data]);

  // Only show records that have at least one feedback/comment field filled
  const reviews = useMemo(() => {
    return allPayments.filter(
      (p) => p.customer_feedback || p.driver_comments || p.admin_comments,
    );
  }, [allPayments]);

  const stats = useMemo(() => ({
    total:            reviews.length,
    withFeedback:     reviews.filter((r) => r.customer_feedback).length,
    withDriverNotes:  reviews.filter((r) => r.driver_comments).length,
    withAdminNotes:   reviews.filter((r) => r.admin_comments).length,
  }), [reviews]);

  const handleExportToExcel = () => {
    const data = reviews.map((p, i) => ({
      "#":                   i + 1,
      "Driver":              p.driver_name || "",
      "Invoice":             p.invoice_no || "",
      "Trip Date":           p.trip_date ? dayjs(p.trip_date).format("DD MMM YYYY") : "",
      "Customer Feedback":   p.customer_feedback || "",
      "Driver Notes":        p.driver_comments || "",
      "Admin Instructions":  p.admin_comments || "",
      "Status":              p.status === "paid" ? "Paid" : "Pending",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Driver Reviews");
    XLSX.writeFile(wb, `driver_reviews_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Driver Reviews & Feedback</Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
          Customer feedback, driver notes, and admin instructions from completed trips
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[
          { label: "Total Reviews", value: stats.total,           icon: <RateReviewOutlinedIcon />, color: BRAND,      bg: "#eef2ff" },
          { label: "Customer Feedback", value: stats.withFeedback, icon: <ChatBubbleOutlineIcon />, color: "#16a34a",  bg: "#f0fdf4" },
          { label: "Driver Notes",  value: stats.withDriverNotes,  icon: <PersonOutlineIcon />,     color: "#f59e0b",  bg: "#fffbeb" },
          { label: "Admin Notes",   value: stats.withAdminNotes,   icon: <ChatBubbleOutlineIcon />, color: "#0ea5e9",  bg: "#f0f9ff" },
        ].map((s, i) => (
          <Card key={i} elevation={0} sx={{ flex: "1 1 140px", border: "1px solid #e2e8f0", borderRadius: 2.5, p: 2, minWidth: 130 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center", color: s.color }}>
                {React.cloneElement(s.icon, { sx: { fontSize: 16 } })}
              </Box>
              <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{s.label}</Typography>
            </Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</Typography>
          </Card>
        ))}
      </Box>

      {/* Search */}
      <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField size="small" placeholder="Search driver, invoice..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
            sx={{ minWidth: 280 }} />
          <Button size="small" variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PrintIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownloadPdf} disabled={pdfLoading}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}>
            {pdfLoading ? "Generating…" : "Print"}
          </Button>
          {reviews.length > 0 && (
            <Button size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={handleExportToExcel}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}>
              Excel
            </Button>
          )}
        </Box>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["#", "Driver", "Invoice", "Trip Date", "Customer Feedback", "Driver Notes", "Admin Instructions", "Status"].map((h) => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : reviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: "center", py: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <RateReviewOutlinedIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
                      <Typography sx={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>No reviews yet</Typography>
                      <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
                        Reviews appear here when feedback or notes are added while completing a trip.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map((p, idx) => (
                  <TableRow key={p.id} sx={{ "&:hover": { bgcolor: "#f8fafc" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{idx + 1}</TableCell>

                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{p.driver_name || "—"}</Typography>
                    </TableCell>

                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND }}>{p.invoice_no || "—"}</Typography>
                    </TableCell>

                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, color: "#374151" }}>
                        {p.trip_date ? dayjs(p.trip_date).format("DD MMM YYYY") : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ ...tdSx, maxWidth: 200 }}>
                      {p.customer_feedback ? (
                        <Tooltip title={p.customer_feedback} arrow placement="top">
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 13, color: "#16a34a", mt: 0.2, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.5,
                              overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                              {p.customer_feedback}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ ...tdSx, maxWidth: 180 }}>
                      {p.driver_comments ? (
                        <Tooltip title={p.driver_comments} arrow placement="top">
                          <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.5,
                            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            {p.driver_comments}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ ...tdSx, maxWidth: 180 }}>
                      {p.admin_comments ? (
                        <Tooltip title={p.admin_comments} arrow placement="top">
                          <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.5,
                            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                            {p.admin_comments}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>—</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={tdSx}>
                      <Chip label={p.status === "paid" ? "Paid" : "Pending"} size="small"
                        sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          color: p.status === "paid" ? "#16a34a" : "#f59e0b",
                          bgcolor: p.status === "paid" ? "#f0fdf4" : "#fffbeb",
                          border: `1px solid ${p.status === "paid" ? "#bbf7d0" : "#fde68a"}`,
                        }} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default DriverReviewsList;
